import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  loadCurrentHistoryAction,
  selectScreenshotAction,
  toggleScreenshotModalAction,
} from '../../../../redux/actions/scheduler';
import { useQuery } from '@apollo/client';
import scheduleHistoryQuery from '../../../../utils/apollo/scheduler/scheduleHistoryQuery';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import KubitTooltip from '../../../../components/KubitTooltip';
import * as ScheduleHistoryUI from './ScheduleHistoryUI';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../../../../path';
import {
  IScheduleHistoryQuery,
  IScheduleHistoryQueryVariables,
  ScheduleRunOutcome,
} from '../../../../utils/apollo/graphqlSchema';
import moment from 'moment';
import { SvgIcon } from '../../../../components/Icon';
import { useAppSelector } from '../../../../redux/hooks';
import { assertCondition } from '../../../../utils/types';
import { globalFormat } from '../../../../utils/formatting';

interface IProps {
  scheduleId: string;
}

export default memo(function ExecutionHistory({ scheduleId }: IProps) {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onScheduleHistoryFetch = useCallback(
    ({ scheduleHistory }: IScheduleHistoryQuery) => {
      dispatch(loadCurrentHistoryAction(scheduleHistory));
    },
    [dispatch],
  );

  const { loading } = useQuery<
    IScheduleHistoryQuery,
    IScheduleHistoryQueryVariables
  >(scheduleHistoryQuery, {
    variables: {
      scheduleId,
    },
    onCompleted: onScheduleHistoryFetch,
  });

  const scheduleHistory = useAppSelector(
    (state) => state.scheduler.currentHistory,
  );

  /**
   * Open the screenshot modal by using index.
   * because a scheduleRun could have null value on screenshotUrl,
   * we need to find the index of the valid screenshot list.
   * @param clickedScreenshotUrl use screenshotUrl to find the index of the valid screenshot list.
   */
  const openScreenshot = (clickedScreenshotUrl: string) => {
    if (!scheduleHistory) {
      return;
    }

    let indexOnValidScreenshot = 0;
    for (const historyRecord of scheduleHistory) {
      if (!historyRecord) {
        continue;
      }
      assertCondition(historyRecord.__typename === 'AnalysesRun');
      if (!historyRecord.screenshotUrl) {
        continue;
      }
      if (historyRecord.screenshotUrl === clickedScreenshotUrl) {
        dispatch(selectScreenshotAction(indexOnValidScreenshot));
        break;
      }
      indexOnValidScreenshot++;
    }
    dispatch(toggleScreenshotModalAction(true));
  };

  const getOutcomeDisplay = (outcome: ScheduleRunOutcome) => {
    if (outcome === ScheduleRunOutcome.Failed) {
      return (
        <ScheduleHistoryUI.FailedAnomaly>
          Failed
        </ScheduleHistoryUI.FailedAnomaly>
      );
    }
    return (
      <ScheduleHistoryUI.OutcomeDot
        $isAnomaly={outcome === ScheduleRunOutcome.Anomaly}
      />
    );
  };
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <>
      <ScheduleHistoryUI.ScheduleHistoryContainer>
        <ScheduleHistoryUI.ScheduleHistoryHeader>
          <ScheduleHistoryUI.ScheduleHistoryGrid>
            <ScheduleHistoryUI.ScheduleHistoryHeaderCell>
              Last Execution
              <ScheduleHistoryUI.StyledBadge color="grey" size="primary">
                {/* display short abbr */}
                {moment().tz(browserTimeZone).zoneAbbr()}
              </ScheduleHistoryUI.StyledBadge>
            </ScheduleHistoryUI.ScheduleHistoryHeaderCell>
            <ScheduleHistoryUI.ScheduleHistoryHeaderCell>
              Anomaly
            </ScheduleHistoryUI.ScheduleHistoryHeaderCell>
            <div />
          </ScheduleHistoryUI.ScheduleHistoryGrid>
        </ScheduleHistoryUI.ScheduleHistoryHeader>
        {loading ? (
          <ScheduleHistoryUI.ScheduleHistoryLoading>
            <ScheduleHistoryUI.StyledLoader active />
          </ScheduleHistoryUI.ScheduleHistoryLoading>
        ) : !loading && scheduleHistory?.length === 0 ? (
          <ScheduleHistoryUI.ScheduleHistoryLoading>
            No records found
          </ScheduleHistoryUI.ScheduleHistoryLoading>
        ) : (
          <ScheduleHistoryUI.ScheduleHistoryContent>
            {scheduleHistory?.map((historyRecord) => {
              if (!historyRecord) {
                return <></>;
              }
              // schedule history will be only called in Analysis schedule and dashboard schedule.
              assertCondition(historyRecord.__typename === 'AnalysesRun');
              const { id, outcome, runAt, entityUrl, screenshotUrl } =
                historyRecord;
              const redirectUrl = entityUrl ? new URL(entityUrl) : null;

              const isDashboard = redirectUrl?.pathname.startsWith(
                appRoutes.dashboard.routePrefix,
              );

              const lastRunAtInBrowserTimezone = globalFormat.formatDate(
                runAt,
                {
                  dateFormat,
                  timeFormat,
                },
              );
              return (
                <ScheduleHistoryUI.ScheduleHistoryGrid key={id}>
                  <ScheduleHistoryUI.ScheduleHistoryCell>
                    {lastRunAtInBrowserTimezone}
                  </ScheduleHistoryUI.ScheduleHistoryCell>
                  <ScheduleHistoryUI.ScheduleHistoryCell $align="center">
                    {getOutcomeDisplay(outcome)}
                  </ScheduleHistoryUI.ScheduleHistoryCell>
                  <ScheduleHistoryUI.ScheduleHistoryRunButtonContainer>
                    <div>
                      {screenshotUrl ? (
                        <KubitTooltip
                          content={`Show ${
                            isDashboard ? 'Report' : 'Chart'
                          } Image`}
                          placement="bottom-end"
                          noOpenDelay={true}
                        >
                          <ScheduleHistoryUI.StyledIconContainer>
                            <SvgIcon
                              name={'picture-icon'}
                              isHoverable
                              onClick={() => openScreenshot(screenshotUrl)}
                            />
                          </ScheduleHistoryUI.StyledIconContainer>
                        </KubitTooltip>
                      ) : (
                        <ScheduleHistoryUI.ScheduleHistoryRunButtonPlaceholder />
                      )}
                    </div>
                    <div>
                      {redirectUrl && (
                        <KubitTooltip
                          content={`Go to ${
                            isDashboard ? 'Dashboard' : 'Report'
                          }`}
                          placement="bottom-end"
                          noOpenDelay={true}
                        >
                          <SvgIcon
                            name="back"
                            isHoverable
                            onClick={() => navigate(redirectUrl.pathname)}
                          />
                        </KubitTooltip>
                      )}
                    </div>
                  </ScheduleHistoryUI.ScheduleHistoryRunButtonContainer>
                </ScheduleHistoryUI.ScheduleHistoryGrid>
              );
            })}
          </ScheduleHistoryUI.ScheduleHistoryContent>
        )}
      </ScheduleHistoryUI.ScheduleHistoryContainer>
    </>
  );
});
